// Hot-reload coordinator (wave 2E). Implements the client side of SPEC.md §7:
//
//   1. Subscribe to `hot-reload` broadcasts from the server.
//   2. status="starting"  → fade overlay in.
//   3. status="ready"     → detach old module, fetch+import new, attach, refresh
//                           non-palette entities through the new renderEntity,
//                           fade overlay out.
//   4. status="failed"    → toast banner; current module stays attached.
//
// Plus the synthetic-initial-load path: if room.state.gameModuleSha is already
// non-empty when we connect (the room was created and bootstrapped before we
// joined), treat that as an implicit `ready` event and load the module without
// a fade — the user's first frame should not start with a black overlay.

import type * as THREE from "three";
import type { Room } from "colyseus.js";
import type { EntityLike, RoomStateLike } from "./types";
import type { EntityRenderer, CustomRenderer } from "./renderer";
import {
  fadeIn as defaultFadeIn,
  fadeOut as defaultFadeOut,
  type FadeTarget,
} from "./fade";

export interface ClientContext {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  state: RoomStateLike;
  sendMessage: (type: string, payload: unknown) => void;
  log: (msg: string) => void;
}

// Mirrors the per-room client.ts contract in SPEC.md §5 + /rooms/template/client.ts.
// We declare it here (rather than import from /rooms/...) to keep the engine
// independent of room modules at type-check time.
export interface LoadedGameModuleClient {
  attach(ctx: ClientContext): void | Promise<void>;
  detach(ctx: ClientContext): void | Promise<void>;
  renderEntity?: CustomRenderer;
}

interface LoadedGameModuleExport {
  default?: () => LoadedGameModuleClient;
}

export interface HotReloadBroadcast {
  sha: string;
  status: "starting" | "ready" | "failed";
  error?: string;
}

export interface HotReloadHandle {
  /** Force-load a specific sha (e.g. on initial state hydration or manual debug). */
  loadModule(sha: string): Promise<void>;
  /** Detach the current per-room client module and dispose its objects. */
  unload(): Promise<void>;
  /** Currently-loaded module sha (or empty string if nothing loaded). */
  currentSha(): string;
  /** Debug helper: run the fade cycle without changing modules. */
  simulateFade(): Promise<void>;
}

export interface HotReloadDeps {
  /** Where the module HTTP endpoint lives. Relative path goes through Vite's proxy in dev. */
  moduleUrlFor: (roomId: string, sha: string) => string;
  /** Dynamic import — overridable for tests. */
  importModule?: (url: string) => Promise<LoadedGameModuleExport>;
  /** Fade overlay element (typically #fade-overlay from the index.html). */
  fadeOverlay?: FadeTarget | null;
  /** Where toasts go (defaults to document.body). */
  toastContainer?: HTMLElement | null;
  /** EntityRenderer to wire renderEntity into and re-render entities through. */
  entityRenderer: EntityRenderer;
  /** Used to schedule re-renders + toast removal (overridable for tests). */
  setTimeoutImpl?: (cb: () => void, ms: number) => unknown;
  /** Logger (defaults to console). */
  log?: (msg: string) => void;
  /** Override the fade-in routine (tests use this to short-circuit timing). */
  fadeIn?: (el: FadeTarget | null) => Promise<void>;
  /** Override the fade-out routine. */
  fadeOut?: (el: FadeTarget | null) => Promise<void>;
}

/**
 * Wires up the hot-reload protocol for a freshly-joined room.
 *
 * Concurrency model: a single in-flight swap. If a `starting` arrives while a
 * swap is in progress for the same sha, it's ignored. If a different `ready`
 * arrives mid-swap, we queue exactly one pending sha — additional arrivals
 * overwrite the queue so we always converge on the latest.
 */
export function setupHotReload(
  room: Room<RoomStateLike>,
  ctx: ClientContext,
  deps: HotReloadDeps,
): HotReloadHandle {
  const log = deps.log ?? ((m: string) => console.log(`[hotreload] ${m}`));
  const importModule = deps.importModule ?? defaultImport;
  const fadeOverlay = deps.fadeOverlay ?? defaultFadeOverlay();
  const toastContainer = deps.toastContainer ?? defaultToastContainer();
  const setTimeoutImpl = deps.setTimeoutImpl ?? ((cb, ms) => setTimeout(cb, ms));
  const fadeIn = deps.fadeIn ?? defaultFadeIn;
  const fadeOut = deps.fadeOut ?? defaultFadeOut;

  const roomId = roomIdOf(room);

  let currentModule: LoadedGameModuleClient | null = null;
  let currentSha = "";
  let inflight = false;
  let pendingSha: string | null = null;
  // True once we've consumed the initial state.gameModuleSha (so the
  // listen callback doesn't fire a redundant fade-less load when the server
  // also broadcasts a `ready` for the same sha).
  let initialLoadConsumed = false;

  async function performSwap(targetSha: string, withFade: boolean): Promise<void> {
    if (!targetSha) return;
    if (targetSha === currentSha) {
      log(`skip: sha ${shortSha(targetSha)} already loaded`);
      return;
    }
    if (inflight) {
      pendingSha = targetSha;
      log(`swap in progress; queued ${shortSha(targetSha)}`);
      return;
    }
    inflight = true;
    try {
      if (withFade) await fadeIn(fadeOverlay);
      await detachCurrent();
      const url = deps.moduleUrlFor(roomId, targetSha);
      log(`fetching ${url}`);
      const mod = await importModule(url);
      const factory = mod.default;
      if (typeof factory !== "function") {
        throw new Error(`module ${shortSha(targetSha)} missing default export factory`);
      }
      const instance = factory();
      await Promise.resolve(instance.attach(ctx));
      currentModule = instance;
      currentSha = targetSha;
      deps.entityRenderer.setCustomRenderer(instance.renderEntity ?? null);
      refreshEntities(ctx.state, deps.entityRenderer);
      if (withFade) await fadeOut(fadeOverlay);
      log(`loaded ${shortSha(targetSha)}`);
    } catch (err) {
      // Restore the overlay if we faded in but never made it back out.
      if (withFade) await fadeOut(fadeOverlay).catch(() => undefined);
      const msg = err instanceof Error ? err.message : String(err);
      log(`load failed for ${shortSha(targetSha)}: ${msg}`);
      showToast(toastContainer, `hot-reload load failed: ${msg}`, setTimeoutImpl);
      // Keep currentModule as-is (the old one is still attached, since detach
      // already ran). If detach succeeded but attach failed, we have NO module
      // loaded — the engine palette renderer continues to handle palette kinds,
      // which is the right degraded mode. currentSha stays at the last known
      // good sha so a retry of the same broken sha still attempts a swap.
      throw err;
    } finally {
      inflight = false;
      if (pendingSha && pendingSha !== currentSha) {
        const next = pendingSha;
        pendingSha = null;
        // Queued swap: fade since this is a real new module.
        void performSwap(next, true).catch(() => undefined);
      } else {
        pendingSha = null;
      }
    }
  }

  async function detachCurrent(): Promise<void> {
    if (!currentModule) return;
    const prev = currentModule;
    currentModule = null;
    deps.entityRenderer.setCustomRenderer(null);
    try {
      await Promise.resolve(prev.detach(ctx));
    } catch (err) {
      log(`detach threw (continuing): ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  room.onMessage("hot-reload", (msg: HotReloadBroadcast) => {
    if (!msg || typeof msg.sha !== "string") {
      log(`ignoring malformed hot-reload broadcast`);
      return;
    }
    if (msg.status === "starting") {
      if (msg.sha === currentSha) {
        log(`skip starting: ${shortSha(msg.sha)} already current`);
        return;
      }
      // Pre-fade on starting so the user doesn't see flicker between starting
      // and ready. The actual swap (with its own fadeIn await) is idempotent
      // — fading something that's already opaque is a no-op.
      void fadeIn(fadeOverlay).catch(() => undefined);
    } else if (msg.status === "ready") {
      void performSwap(msg.sha, true).catch(() => undefined);
    } else if (msg.status === "failed") {
      const text = msg.error ? `hot-reload failed: ${msg.error}` : `hot-reload failed`;
      log(text);
      showToast(toastContainer, text, setTimeoutImpl);
      // Also try to revert the overlay if we'd already faded in for a "starting".
      void fadeOut(fadeOverlay).catch(() => undefined);
    } else {
      log(`ignoring hot-reload with unknown status: ${String((msg as { status?: unknown }).status)}`);
    }
  });

  // Initial load: if state.gameModuleSha is already set when we join (the room
  // was created/bootstrapped before us) we load the module without a fade.
  // Use .listen() so we catch the first non-empty value even if state hasn't
  // hydrated yet.
  const state = room.state;
  if (state) {
    const initialSha = readSha(state);
    if (initialSha) {
      initialLoadConsumed = true;
      void performSwap(initialSha, false).catch(() => undefined);
    } else if (typeof state.listen === "function") {
      state.listen("gameModuleSha", (value: unknown) => {
        if (initialLoadConsumed) return;
        const sha = typeof value === "string" ? value : "";
        if (!sha) return;
        initialLoadConsumed = true;
        void performSwap(sha, false).catch(() => undefined);
      });
    }
  }

  return {
    loadModule(sha: string): Promise<void> {
      return performSwap(sha, true);
    },
    unload(): Promise<void> {
      return detachCurrent();
    },
    currentSha(): string {
      return currentSha;
    },
    async simulateFade(): Promise<void> {
      await fadeIn(fadeOverlay);
      await fadeOut(fadeOverlay);
    },
  };
}

function readSha(state: RoomStateLike): string {
  const v = state.gameModuleSha;
  return typeof v === "string" ? v : "";
}

function refreshEntities(state: RoomStateLike, renderer: EntityRenderer): void {
  if (!state?.entities?.forEach) return;
  state.entities.forEach((entity: EntityLike) => {
    try {
      renderer.updateEntity(entity);
    } catch (err) {
      console.warn(
        `[hotreload] failed to re-render entity ${entity?.id ?? "?"}:`,
        err,
      );
    }
  });
}

function shortSha(sha: string): string {
  return sha.length > 8 ? sha.slice(0, 8) : sha;
}

function defaultImport(url: string): Promise<LoadedGameModuleExport> {
  // @vite-ignore tells Vite not to try to statically analyze this dynamic URL.
  return import(/* @vite-ignore */ url) as Promise<LoadedGameModuleExport>;
}

function defaultFadeOverlay(): FadeTarget | null {
  if (typeof document === "undefined") return null;
  return document.querySelector<HTMLDivElement>("#fade-overlay");
}

function defaultToastContainer(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  return document.body ?? null;
}

const TOAST_TTL_MS = 4000;

function showToast(
  container: HTMLElement | null,
  text: string,
  setTimeoutImpl: (cb: () => void, ms: number) => unknown,
): void {
  if (!container || typeof document === "undefined") return;
  const el = document.createElement("div");
  el.textContent = text;
  el.dataset.hotreloadToast = "1";
  Object.assign(el.style, {
    position: "fixed",
    top: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    padding: "10px 18px",
    background: "rgba(180, 30, 30, 0.92)",
    color: "#fff",
    fontFamily: "inherit",
    fontSize: "13px",
    borderRadius: "4px",
    zIndex: "100",
    boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
    pointerEvents: "none",
  } satisfies Partial<CSSStyleDeclaration>);
  container.appendChild(el);
  setTimeoutImpl(() => {
    el.remove();
  }, TOAST_TTL_MS);
}

function roomIdOf(room: Room<RoomStateLike>): string {
  // colyseus.js exposes `roomId` (the canonical matchmaker id).
  // `id` is a legacy getter; prefer roomId.
  const r = room as unknown as { roomId?: string; id?: string };
  return r.roomId ?? r.id ?? "";
}
